import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, EnvelopeSimple, MapPin, Buildings } from "@phosphor-icons/react";
import { Reveal, SectionLabel } from "./motion";
import { useLang } from "@/LanguageContext";
import { COMPANY } from "@/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useLang();
  const f = t.contact.form;
  const [form, setForm] = useState({ name: "", email: "", organisation: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(f.success);
      setForm({ name: "", email: "", organisation: "", message: "" });
    } catch (err) {
      toast.error(f.error);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/15 py-3 text-white placeholder-white/40 focus:border-gold outline-none transition-colors duration-300";

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 lg:py-36 bg-surface">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <Reveal><SectionLabel>{t.contact.eyebrow}</SectionLabel></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white mt-6 tracking-[-0.01em]">{t.contact.title}</h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="text-white/60 leading-relaxed mt-6 max-w-md">{t.contact.body}</p>
          </Reveal>
          <Reveal delay={0.18}>
            <div className="mt-12 space-y-5">
              <div className="text-xs uppercase tracking-[0.28em] text-gold/80">{t.contact.locationLabel} · {COMPANY.coordinates}</div>
              <a href={`mailto:${COMPANY.email}`} data-testid="contact-email" className="flex items-center gap-4 text-white/80 hover:text-gold transition-colors duration-300 whitespace-nowrap">
                <EnvelopeSimple size={20} className="text-gold shrink-0" /> {COMPANY.email}
              </a>
              <div className="flex items-center gap-4 text-white/80">
                <MapPin size={20} className="text-gold shrink-0" /> {COMPANY.address}
              </div>
              <div className="flex items-center gap-4 text-white/80">
                <Buildings size={20} className="text-gold shrink-0" /> VAT {COMPANY.vat}
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1} y={50}>
            <form onSubmit={onSubmit} data-testid="contact-form" className="border border-white/10 rounded-sm p-8 lg:p-12 bg-navy/40">
              <div className="grid sm:grid-cols-2 gap-8">
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/40">{f.name}</label>
                  <input data-testid="contact-name" name="name" value={form.name} onChange={onChange} required className={inputCls} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/40">{f.email}</label>
                  <input data-testid="contact-email-input" type="email" name="email" value={form.email} onChange={onChange} required className={inputCls} />
                </div>
              </div>
              <div className="mt-8">
                <label className="text-xs uppercase tracking-widest text-white/40">{f.organisation}</label>
                <input data-testid="contact-org" name="organisation" value={form.organisation} onChange={onChange} className={inputCls} />
              </div>
              <div className="mt-8">
                <label className="text-xs uppercase tracking-widest text-white/40">{f.message}</label>
                <textarea data-testid="contact-message" name="message" value={form.message} onChange={onChange} required rows={4} className={`${inputCls} resize-none`} />
              </div>
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={loading}
                className="btn-gold mt-10 inline-flex items-center gap-2 bg-gold text-navy font-semibold rounded-full pl-7 pr-5 py-4 disabled:opacity-60"
              >
                <span>{loading ? f.sending : f.submit}</span>
                <ArrowUpRight weight="bold" size={18} className="relative z-10" />
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
